import React, { useEffect, useRef, useState } from "react";
import { useVault } from "@/context/VaultContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PasswordField } from "./PasswordField";
import { ResetVaultDialog } from "./ResetVaultDialog";
import { errorMessage } from "@/lib/api";
import { normalizeRecoveryCode } from "@/lib/vaultCrypto";
import { Lock, Loader2, KeyRound, LifeBuoy, RotateCcw, Info } from "lucide-react";

export const UnlockScreen = ({ title = "Hvelvet er låst", description = "Skriv hovedpassordet for å låse opp. Nøkkelen utledes lokalt i nettleseren." }) => {
  const { unlock, busy, meta, lockedAt } = useVault();
  const [mode, setMode] = useState("password");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [resetOpen, setResetOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setSecret(""); setError(""); }, [mode]);
  useEffect(() => { const t = setTimeout(() => inputRef.current?.focus?.(), 50); return () => clearTimeout(t); }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!secret) return;
    setError("");
    try {
      const ok = await unlock(secret, mode === "recovery");
      if (!ok) {
        setAttempts((a) => a + 1);
        setError(mode === "recovery" ? "Feil gjenopprettingskode. Sjekk at alle tegnene er riktige – koden har 24 tegn i seks grupper." : "Feil hovedpassord. Prøv igjen.");
        setSecret("");
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div className="mx-auto max-w-md pt-4 sm:pt-10" data-testid="vault-unlock-screen">
      <Card>
        <CardHeader className="items-center text-center">
          <div className="h-14 w-14 rounded-2xl bg-accent text-accent-foreground flex items-center justify-center mb-2"><Lock className="h-7 w-7" /></div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{lockedAt && Date.now() - lockedAt < 60000 && attempts === 0 ? "Hvelvet ble låst. " : ""}{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" noValidate>
            {mode === "password" ? (
              <div className="space-y-1.5">
                <Label htmlFor="unlock-master">Hovedpassord</Label>
                <PasswordField id="unlock-master" value={secret} onChange={(e) => setSecret(e.target.value)} testId="unlock-master" sensitive={false} autoComplete="current-password" />
                <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="unlock-master-hint"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>Hovedpassordet valgte du selv da hvelvet ble satt opp. Det sendes aldri på e-post eller SMS, og er ikke det samme som innloggingspassordet.</span></p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="unlock-recovery">Gjenopprettingskode</Label>
                <Input id="unlock-recovery" ref={inputRef} value={secret} onChange={(e) => setSecret(normalizeRecoveryCode(e.target.value))} placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" className="font-mono tracking-widest uppercase" autoComplete="off" spellCheck={false} data-testid="unlock-recovery-input" />
                {meta?.recovery?.hint ? <p className="text-xs text-muted-foreground">Hint: begynner med {meta.recovery.hint}</p> : null}
                <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="unlock-recovery-hint"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>Koden du fikk (og lastet ned eller skrev av) da hvelvet ble satt opp. Etter opplåsing blir du bedt om å velge et nytt hovedpassord.</span></p>
              </div>
            )}
            {error ? <p className="text-sm text-destructive" role="alert" data-testid="unlock-error">{error}</p> : null}
            <Button type="submit" className="w-full" disabled={busy || !secret} data-testid="unlock-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />} {busy ? "Låser opp …" : "Lås opp"}</Button>
            <div className="flex flex-col items-center gap-1 text-center">
              {mode === "password" ? (
                meta?.has_recovery ? <Button type="button" variant="link" size="sm" className="text-muted-foreground" onClick={() => setMode("recovery")} data-testid="unlock-use-recovery"><LifeBuoy className="h-3.5 w-3.5 mr-1.5" /> Glemt hovedpassordet? Bruk gjenopprettingskode</Button> : null
              ) : (
                <Button type="button" variant="link" size="sm" className="text-muted-foreground" onClick={() => setMode("password")} data-testid="unlock-use-password">Bruk hovedpassord i stedet</Button>
              )}
              <Button type="button" variant="link" size="sm" className="text-muted-foreground" onClick={() => setResetOpen(true)} data-testid="unlock-reset-vault"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Har du verken hovedpassord eller kode? Nullstill hvelvet</Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <p className="mt-4 text-center text-xs text-muted-foreground">Hvelvet låses automatisk etter {meta?.settings?.auto_lock_minutes || 5} min uten aktivitet.</p>
      <ResetVaultDialog open={resetOpen} onOpenChange={setResetOpen} />
    </div>
  );
};
